import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CITY_EVENTS_QUERY, EVENT_CITIES_QUERY, REGISTER_FOR_EVENT_MUTATION, UNREGISTER_FROM_EVENT_MUTATION } from '../graphql';
import { groupCover } from '../config/groupImages';

const CATEGORIES = ['All', 'Outdoor Activity', 'Sports', 'Music', 'Art & Design', 'Photography', 'Cooking', 'Dance', 'Reading', 'Gaming', 'Science', 'Language', 'Crafts', 'Cultural', 'Other'];

function when(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
const priceLabel = p => (p > 0 ? `⭐ ${p}` : 'Free');

// A Meetup-style event card with a themed cover derived from the host group's type.
function EventCard({ ev }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const cover = groupCover({ category: ev.groupCategory, persona: ev.persona, broadCategory: ev.broadCategory });
  const [register, { loading: reg }] = useMutation(REGISTER_FOR_EVENT_MUTATION, { refetchQueries: [{ query: CITY_EVENTS_QUERY }], onError: e => alert(e.message) });
  const [unregister, { loading: unreg }] = useMutation(UNREGISTER_FROM_EVENT_MUTATION, { refetchQueries: [{ query: CITY_EVENTS_QUERY }], onError: e => alert(e.message) });
  const full = ev.capacity > 0 && ev.registrationCount >= ev.capacity && !ev.isRegistered;
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
      onClick={() => navigate(`/group/${ev.groupId}`)}>
      <div style={{ height: 120, background: cover.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        <span style={{ fontSize: 52, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))' }}>{cover.emoji}</span>
        {ev.status === 'LIVE' && <span className="badge" style={{ position: 'absolute', top: 8, left: 8, background: 'var(--danger)', color: '#fff' }}>● LIVE</span>}
      </div>
      <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>{when(ev.startsAt)}</div>
        <div style={{ fontWeight: 800, fontSize: 'var(--font-base)', lineHeight: 1.3 }}>{ev.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>by {ev.groupName} · {ev.groupCity}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', flexWrap: 'wrap' }}>
          <span className="badge badge-dim">{priceLabel(ev.ticketPrice)}</span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>👥 {ev.registrationCount}{ev.capacity > 0 ? `/${ev.capacity}` : ''} going</span>
          <div style={{ flex: 1 }} />
          {!currentUser ? null : ev.isRegistered ? (
            <button className="btn btn-ghost btn-sm" disabled={unreg} onClick={e => { e.stopPropagation(); unregister({ variables: { eventId: ev.id } }); }}>✓ Going</button>
          ) : (
            <button className="btn btn-primary btn-sm" disabled={reg || full} onClick={e => { e.stopPropagation(); register({ variables: { eventId: ev.id } }); }}>{full ? 'Full' : 'RSVP'}</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { currentUser } = useAuth();
  const [city, setCity] = useState(currentUser?.location?.city || 'Bangalore');
  const [category, setCategory] = useState('All');

  const { data: citiesData } = useQuery(EVENT_CITIES_QUERY);
  const { data, loading } = useQuery(CITY_EVENTS_QUERY, { variables: { city: city || undefined, limit: 150 }, fetchPolicy: 'cache-and-network' });

  const cities = useMemo(() => {
    const set = new Set(['Bangalore', ...(citiesData?.eventCities || [])]);
    if (currentUser?.location?.city) set.add(currentUser.location.city);
    return [...set].filter(Boolean);
  }, [citiesData, currentUser]);

  const events = (data?.cityEvents || []).filter(e => category === 'All' || e.groupCategory === category);
  // Group into category sections (Meetup style).
  const sections = useMemo(() => {
    const by = {};
    for (const e of events) { const k = e.groupCategory || 'Other'; (by[k] = by[k] || []).push(e); }
    return Object.entries(by).sort((a, b) => b[1].length - a[1].length);
  }, [events]);

  return (
    <div className="page">
      <div style={{ background: 'linear-gradient(135deg,rgba(26,115,232,0.08),rgba(139,92,246,0.05))', borderBottom: '1px solid var(--border)', padding: '32px 0 24px' }}>
        <div className="container">
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>📅 Events in {city || 'your city'}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>Sports & hobby meetups happening near you — trek, shoot, jam, learn. RSVP in a tap.</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select className="input" style={{ width: 'auto', minWidth: 160 }} value={city} onChange={e => setCity(e.target.value)}>
              {cities.map(c => <option key={c} value={c}>📍 {c}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '20px 16px' }}>
        {/* Category chips */}
        <div style={{ overflowX: 'auto', marginBottom: 20, paddingBottom: 4 }}>
          <div style={{ display: 'flex', gap: 8, minWidth: 'max-content' }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)} className="btn btn-sm"
                style={{ background: category === c ? 'var(--primary)' : 'var(--surface)', color: category === c ? '#fff' : 'var(--text-muted)', border: `2px solid ${category === c ? 'var(--primary)' : 'var(--border)'}`, flexShrink: 0 }}>{c}</button>
            ))}
          </div>
        </div>

        {loading && events.length === 0 ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-title">No upcoming events in {city}</div>
            <div className="empty-state-desc">Try another city, or start a group and host the first one!</div>
          </div>
        ) : category === 'All' ? (
          sections.map(([cat, evs]) => (
            <div key={cat} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 800 }}>{cat}</h2>
                {evs.length > 3 && <button className="btn btn-ghost btn-sm" onClick={() => setCategory(cat)}>See all {evs.length} →</button>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {evs.slice(0, 3).map(ev => <EventCard key={ev.id} ev={ev} />)}
              </div>
            </div>
          ))
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {events.map(ev => <EventCard key={ev.id} ev={ev} />)}
          </div>
        )}
      </div>
    </div>
  );
}
